import React, { useRef, useEffect, useCallback } from "react";
import * as d3 from "d3";
import colors from "../designTokens/colors.esm";
import { useTranslation } from "react-i18next";

// Normalize date to start of the day
const normalizeDate = (date) => {
	const normalized = new Date(date);
	normalized.setUTCHours(0, 0, 0, 0);
	return normalized;
};

const BarChartDate = ({
	data,
	width,
	height,
	fillColor,
	axisColor,
	tickColor,
	xTicks = 5,
	yTicks = 3,
	dateFormat = "%d",
	onBarClick,
	isMobile = false,
}) => {
	const svgRef = useRef();
	const containerRef = useRef();
	const { t } = useTranslation();
	
	// 현지화된 날짜 포맷터
	const formatLocalizedDate = useCallback((date) => {
		const d = new Date(date);
		const month = d.getMonth() + 1;
		const day = d.getDate();
		
		const monthName = t(`common.month.${month}`);
		return `${monthName} ${day}`;
	}, [t]);
	
	// 실시간 차트 업데이트 함수 - D3.js 반응형 패턴
	const updateChart = useCallback(() => {
		if (!data || data.length === 0) return;

		const container = containerRef.current;
		const svg = d3.select(svgRef.current);
		
		// 컨테이너 실제 크기 측정
		const containerWidth = container ? container.offsetWidth : (typeof width === 'number' ? width : 300);
		const actualWidth = containerWidth;
		
		// 반응형 마진 계산 (하단 tick 영역 확보)
		const margin = { 
			top: 48, 
			right: isMobile ? 16 : 24, 
			bottom: 48, // tick 텍스트가 잘리지 않도록 증가
			left: isMobile ? 32 : 48 
		};

		const innerWidth = actualWidth - margin.left - margin.right;
		const innerHeight = height - margin.top - margin.bottom;

		// SVG 크기 및 viewBox 설정 (100% 반응형)
		svg.attr("width", "100%")
			.attr("height", height)
			.attr("viewBox", `0 0 ${actualWidth} ${height}`)
			.attr("preserveAspectRatio", "xMidYMid meet");

		// 차트 영역 선택 또는 생성
		let chartArea = svg.select("g.chart-area");
		if (chartArea.empty()) {
			chartArea = svg.append("g").attr("class", "chart-area");
			
			// 축 그룹 생성
			chartArea.append("g").attr("class", "x-axis");
			chartArea.append("g").attr("class", "y-axis");
		}

		// 차트 영역 위치 업데이트 (빠른 transition)
		chartArea.transition()
			.duration(150)
			.attr("transform", `translate(${margin.left},${margin.top})`);

		// 스케일 생성 - scaleBand로 자동 바 너비 계산
		const xScale = d3.scaleBand()
			.domain(data.map(d => d.x))
			.range([0, innerWidth])
			.padding(isMobile ? 0.4 : 0.3); // 패딩으로 간격 조절

		// 시간 축용 별도 스케일 (축 표시용)
		const xTimeScale = d3.scaleTime()
			.domain([
				normalizeDate(d3.min(data, (d) => d.x)),
				normalizeDate(d3.max(data, (d) => d.x)),
			])
			.range([0, innerWidth]);

		const yScale = d3.scaleLinear()
			.domain([0, d3.max(data, (d) => d.y)])
			.range([innerHeight, 0]);

		// 축 생성 (반응형 틱 개수) - 시간 축 사용
		const xAxis = d3.axisBottom(xTimeScale)
			.ticks(isMobile ? Math.min(xTicks, 3) : xTicks)
			.tickFormat(formatLocalizedDate)
			.tickSize(6); // X축 틱 마크 표시

		const yAxis = d3.axisLeft(yScale)
			.ticks(isMobile ? Math.min(yTicks, 3) : yTicks)
			.tickFormat(d3.format(".0s"))
			.tickSize(-innerWidth); // Y축 그리드 라인 유지

		// 축 업데이트 (빠른 transition)
		chartArea.select(".x-axis")
			.transition()
			.duration(250)
			.attr("transform", `translate(0,${innerHeight})`)
			.call(xAxis)
			.attr("color", axisColor)
			.selectAll(".tick")
			.attr("color", tickColor)
			.select("line")
			.attr("stroke-width", 0.5);

		// X축 텍스트 크기 조정
		chartArea.select(".x-axis")
			.selectAll("text")
			.style("font-size", isMobile ? "12px" : "14px")
			.style("font-weight", "500");

		chartArea.select(".y-axis")
			.transition()
			.duration(250)
			.call(yAxis)
			.attr("color", axisColor)
			.selectAll(".tick")
			.attr("color", tickColor)
			.select("line")
			.attr("stroke-width", 0.5);

		// Y축 틱 마크 제거 (그리드 라인은 유지)
		chartArea.select(".y-axis")
			.selectAll(".tick")
			.select("line")
			.style("stroke", (_, i, nodes) => {
				// 그리드 라인 (긴 선)은 유지, 틱 마크 (짧은 선)는 제거
				const line = d3.select(nodes[i]);
				const x2 = line.attr("x2");
				return Math.abs(x2) > 10 ? tickColor : "none";
			});

		// Y축 텍스트 크기 조정
		chartArea.select(".y-axis")
			.selectAll("text")
			.style("font-size", isMobile ? "12px" : "14px")
			.style("font-weight", "500");

		// 도메인 라인 제거
		chartArea.selectAll(".domain").remove();

		// 상위 3개 값 계산
		const sortedData = [...data].sort((a, b) => b.y - a.y);
		const top3Values = sortedData.slice(0, 3).map((d) => d.y);

		// 툴팁 생성 (body에 생성하여 오버플로우 방지)
		const tooltip = d3.select("body").selectAll(".d3-tooltip")
			.data([null])
			.join("div")
			.attr("class", "d3-tooltip")
			.style("opacity", 0)
			.style("position", "fixed")
			.style("background-color", "white")
			.style("border", "1px solid #ddd")
			.style("padding", "10px")
			.style("border-radius", "5px")
			.style("pointer-events", "none")
			.style("z-index", "1000");

		// 바 차트 업데이트 (scaleBand 사용)
		const bars = chartArea.selectAll(".bar").data(data, (d) => d.x);

		// 기존 바 업데이트 (크기 변경 시 빠른 transition)
		bars.transition()
			.duration(300)
			.ease(d3.easeQuadOut)
			.attr("x", (d) => xScale(d.x))
			.attr("y", (d) => yScale(d.y))
			.attr("width", xScale.bandwidth())
			.attr("height", (d) => innerHeight - yScale(d.y))
			.attr("fill", (d) => (top3Values.includes(d.y) ? colors.blue[5] : fillColor));

		// 새 바 추가
		bars.enter()
			.append("rect")
			.attr("class", "bar")
			.attr("x", (d) => xScale(d.x))
			.attr("y", innerHeight)
			.attr("width", xScale.bandwidth())
			.attr("height", 0)
			.attr("fill", (d) => (top3Values.includes(d.y) ? colors.blue[5] : fillColor))
			.transition()
			.duration(300)
			.delay((_, i) => i * 20)
			.ease(d3.easeQuadOut)
			.attr("y", (d) => yScale(d.y))
			.attr("height", (d) => innerHeight - yScale(d.y));

		// 바 제거
		bars.exit()
			.transition()
			.duration(200)
			.attr("y", innerHeight)
			.attr("height", 0)
			.remove();

		// 이벤트 핸들러 추가
		chartArea.selectAll(".bar")
			.on("click", (_, d) => {
				tooltip.transition().duration(200).style("opacity", 0);
				if (onBarClick) {
					const clickedDate = new Date(d.x);
					onBarClick(clickedDate);
				}
			})
			.on("mouseover", (event, d) => {
				tooltip.transition().duration(200).style("opacity", 0.9);
				tooltip.html(`Date: ${formatLocalizedDate(new Date(d.x))}<br/>Value: ${d.y}`)
					.style("left", (event.pageX + 10) + "px")
					.style("top", (event.pageY - 28) + "px");
			})
			.on("mouseout", () => {
				tooltip.transition().duration(500).style("opacity", 0);
			});

	}, [data, width, height, fillColor, axisColor, tickColor, xTicks, yTicks, onBarClick, isMobile, formatLocalizedDate]);

	// 초기 설정 및 데이터 변경 시 업데이트
	useEffect(() => {
		updateChart();
	}, [updateChart]);

	// 리사이즈 이벤트 핸들러 (setTimeout 제거)
	useEffect(() => {
		let rafId;
		
		const handleResize = () => {
			// requestAnimationFrame으로 브라우저 렌더링 최적화
			cancelAnimationFrame(rafId);
			rafId = requestAnimationFrame(() => {
				updateChart();
			});
		};

		const resizeObserver = new ResizeObserver(handleResize);
		if (containerRef.current) {
			resizeObserver.observe(containerRef.current);
		}

		window.addEventListener('resize', handleResize);
		window.addEventListener('orientationchange', handleResize);

		return () => {
			cancelAnimationFrame(rafId);
			resizeObserver.disconnect();
			window.removeEventListener('resize', handleResize);
			window.removeEventListener('orientationchange', handleResize);
		};
	}, [updateChart]);

	return (
		<div 
			ref={containerRef}
			style={{ 
				position: "relative", 
				width: '100%', 
				height: height,
				overflow: 'hidden'
			}}
		>
			<svg ref={svgRef}></svg>
		</div>
	);
};

export default BarChartDate;